import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "../../../styles/pages/ouber.scss";
import { Tag, Space, Typography, Alert, Input, Select, Row, Col, DatePicker, Button } from "antd";
import { EyeOutlined, EditOutlined, SearchOutlined, SyncOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import PvTable from "../../../components/Table/PvTable";
import DataView from "../../../components/DataView/DataView";
import PvCardList from "../../../components/DataView/PvCardList";
import AdminViewTripRequestModal from "../../../components/Ouber/modals/admin/AdminViewTripRequestModal";
import AdminLinkTripModal from "../../../components/Ouber/modals/admin/AdminLinkTripModal";
import AdminEditRequestModal from "../../../components/Ouber/modals/admin/AdminEditRequestModal";
import { showErrorToast, showSuccessToast } from "../../../components/Ouber/ToastNotification";
import { useOuberService } from "../../../services";
import { mapUnitToAcronym } from "../../../utils/ouberUtils";
import dayjs from "dayjs";

/**
 * The purpose of this page is to allow administrators to review, 
 * approve, or decline individual trip requests from employees.
 */
const AdminManageTripReqPage = () => {
  const { dataView } = useSelector((state) => state.generalSlice);
  const { details: profile, account_types = [] } = useSelector((state) => state.accountDetailSlice);

  const userUnits = useMemo(() => {
    if (!profile?.positions) return [];
    const todayStr = dayjs().format("YYYY-MM-DD");
    return profile.positions
      .filter(p => {
        const isAO = [
          'Administrative Officer I',
          'Administrative Officer II',
          'Administrative Officer III',
          'Administrative Officer IV',
          'Administrative Officer V',
        ].includes(p.position_name);
        if (!isAO) return false;

        const endDate = p.pivot?.position_end_date || p.position_end_date;
        if (!endDate) return true;
        return endDate >= todayStr;
      })
      .map(p => (p.unit ? mapUnitToAcronym(p.unit) : null))
      .filter(Boolean);
  }, [profile]);
  const isSuperAdmin = profile?.account_type === "Super Admin" || account_types.includes("superadmin") || account_types.includes("xadmin");

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isLinkModalVisible, setIsLinkModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  const [filterName, setFilterName] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterDate, setFilterDate] = useState(null);


  const { service: ouberService, loading } = useOuberService();
  const [data, setData] = useState([]);

  // Fetches trip requests from the service with filters for name, location, status, and date
  const fetchRequests = useCallback(async () => {
    try {
      const params = { 
        search_name: filterName || undefined,
        search_location: filterLocation || undefined,
        status: filterStatus || undefined,
        trip_date: filterDate ? filterDate.format("YYYY-MM-DD") : undefined,
        scope: "unit"
      };
      
      const response = await ouberService.getTripRequests(params);
      const requestsListRaw = Array.isArray(response) ? response : (response?.data || []);
      const requestsList = requestsListRaw.filter(r => r !== null && r !== undefined);

      const reqs = requestsList.map(r => ({ 
        ...r, 
        key: (r.trip_request_id || Math.random()).toString(), 
        id: (r.trip_request_id || "").toString(), 
        trip_date: r.trip_date ? r.trip_date.split("T")[0] : null 
      }));
      
      // Formats raw backend request data into a structure suitable for UI display
      setData(reqs);
    } catch (err) {
      showErrorToast("Fetch Error", "Failed to load trip requests.");
    }
  }, [filterName, filterLocation, filterStatus, filterDate, ouberService]);

  const fetchRequestsRef = useRef(fetchRequests);
  useEffect(() => {
    fetchRequestsRef.current = fetchRequests;
  }, [fetchRequests]);

  // Executes a debounced fetch whenever any of the filter values are updated by the admin
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchRequestsRef.current();
    }, 500);
    return () => clearTimeout(handler);
  }, [filterName, filterLocation, filterStatus, filterDate]);

  const filteredData = data;

  const columns = [
    { title: "Request ID", dataIndex: "id", key: "id", align: "center" },
    {
      title: "Request Status",
      dataIndex: "request_status",
      key: "request_status",
      align: "center",
      render: (status) => {
        let tagClass = "tag-pending";

        if (status === "Requested to Join") {
          tagClass = "tag-pending";
        } else if (status === "Approved by AO") {
          tagClass = "tag-approved";
        } else if (status === "Completed Trip") {
          tagClass = "tag-completed";
        } else if (status === "Declined by AO" || status === "Cancelled by AO" || status === "Cancelled by User") {
          tagClass = "tag-cancelled";
        }
        
        return (
          <Tag
            className={tagClass}
            style={{
              borderRadius: "12px",
              minWidth: "80px",
              textAlign: "center",
            }}
          >
            {status}
          </Tag>
        );
      },
    },
    { 
      title: "Requestor Name", 
      dataIndex: "employee", 
      key: "employee", 
      align: "center",
      render: (emp, record) => record.external_passenger_name || (emp ? `${emp.first_name} ${emp.last_name}` : "N/A")
    },
    { 
      title: "Unit", 
      dataIndex: "unit", 
      key: "unit", 
      align: "center",
      render: (unit) => unit || "N/A"
    },

    { title: "Trip Date", dataIndex: "trip_date", key: "trip_date", align: "center" },
    {
      title: "Pickup Venue",
      dataIndex: "pickup_venue",
      key: "pickup_venue",
      align: "center",
    },
    { 
      title: "Destination", 
      dataIndex: "dropoff_location", 
      key: "dropoff_location", 
      align: "center",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle" style={{ display: 'flex', justifyContent: 'center' }}>
          <EyeOutlined 
            style={{ fontSize: "18px", cursor: "pointer", color: "#1890ff" }} 
            onClick={() => {
              setSelectedRequest(record);
              setIsViewModalVisible(true);
            }} 
          />
          {record.trip_id && (isSuperAdmin || (!["Completed Trip", "Declined by AO", "Cancelled by AO", "Cancelled by User"].includes(record.request_status) && 
           (userUnits.includes(record.unit) || (record.trip && userUnits.includes(record.trip.organizing_unit))))) && (
            <EditOutlined 
              style={{ fontSize: "18px", cursor: "pointer", color: "#52c41a" }} 
              onClick={() => {
                setSelectedRequest(record);
                setIsEditModalVisible(true);
              }} 
            />
          )}
        </Space>
      ),
    },
    { title: "Remarks", dataIndex: "remarks", key: "remarks", align: "center" },
  ];

  return (
    <div className="ouber-page-container">
      <Space className="ouber-page-header">
        <Typography.Title level={3} className="ouber-page-title">
          Manage Trip Requests
        </Typography.Title>
      </Space>

      <Alert
        className="ouber-alert"
        message={<span className="ouber-alert-title">Manage Trip Requests</span>}
        description={
          <span className="ouber-alert-desc">
            Approve, decline, and edit trip requests here. Requests are based on the trips you've made or requests initiated by employees in your unit. 
          </span>
        }
        type="info"
        showIcon
      />
      
      <div className="ouber-filter-container">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Requestor Name" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Pickup or Destination" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <DatePicker 
              placeholder="Filter by Date"
              style={{ width: "100%" }}
              format="MM-DD-YYYY"
              value={filterDate}
              onChange={(date) => setFilterDate(date)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Status"
              style={{ width: "100%" }}
              allowClear
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              options={[
                { value: "Completed Trip", label: "Completed Trip" },
                { value: "Approved by AO", label: "Approved by AO" },
                { value: "Requested to Join", label: "Requested to Join" },
                { value: "Declined by AO", label: "Declined by AO" },
                { value: "Cancelled by AO", label: "Cancelled by AO" },
                { value: "Cancelled by User", label: "Cancelled by User" }
              ]}
            />
          </Col>
        </Row>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "12px" }}>
        <DataView />
        <Button 
          icon={<SyncOutlined />} 
          onClick={fetchRequests} 
          loading={loading}
          className="ouber-btn-primary"
          style={{ marginBottom: 0 }}
        >
          Sync Data
        </Button>
      </div>

      {dataView === "table" ? (
        <PvTable
          data={filteredData}
          columns={columns}
          loading={loading}
          onRowClick={() => {}}
        />
      ) : (
        <PvCardList
          data={filteredData}
          columns={columns}
          onRowClick={() => {}}
          loading={loading}
          maxActiveRow={filteredData.length}
        />
      )}

      <AdminViewTripRequestModal
        isOpen={isViewModalVisible}
        onClose={() => setIsViewModalVisible(false)}
        requestData={selectedRequest}
        onEdit={(req) => {
          setIsViewModalVisible(false);
          setIsEditModalVisible(true);
        }}
        onCreateLinkedTrip={(req) => {
          setIsViewModalVisible(false);
          setIsLinkModalVisible(true);
        }}
        onDecline={async (req) => {
          try {
            await ouberService.updateRequestStatus(req.id, { request_status: "Declined by AO" });
            showSuccessToast("Success", "Trip request has been declined.");
            setIsViewModalVisible(false);
            fetchRequests();
          } catch (e) {
            const errorMsg = e.response?.data?.message || "Could not decline request.";
            showErrorToast("Error", errorMsg);
          }
        }}
      />

      <AdminLinkTripModal
        open={isLinkModalVisible}
        onCancel={() => setIsLinkModalVisible(false)}
        requestData={selectedRequest}
        onSave={async (newTrip) => {
          try {
            await ouberService.createTrip(newTrip);
            showSuccessToast("Success", "Trip successfully created for this request.");
            setIsLinkModalVisible(false);
            fetchRequests();
          } catch (e) {
            const errorMsg = e.response?.data?.message || "Could not process linked trip.";
            showErrorToast("Error", errorMsg);
          }
        }}
      />

      <AdminEditRequestModal
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        passengerData={selectedRequest}
        onSave={async (updatedData) => {
          try {
            // Assuming this endpoint allows partial updates on the request
            await ouberService.updateRequestStatus(selectedRequest.id, updatedData);
            showSuccessToast("Success", "Request details successfully updated.");
            setIsEditModalVisible(false);
            fetchRequests();
          } catch (e) {
            const errorMsg = e.response?.data?.message || "Could not update request details.";
            showErrorToast("Error", errorMsg);
          }
        }}
      />
    </div>
  );
};

export default AdminManageTripReqPage;
